import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';
import { social } from './paths.js';
import type { Person } from './ShareDialog.js';

interface Summary {
  notifications: number;
  messages: number;
  friendRequests: number;
  invitations: number;
}

interface Note {
  id: number;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  read: boolean;
}

interface Friends {
  friends: Person[];
  incoming: Person[];
  outgoing: Person[];
}

interface Convo {
  friend: Person;
  last: { text: string; at: string; fromMe: boolean };
  unread: number;
}

interface Msg {
  id: number;
  fromMe: boolean;
  text: string;
  at: string;
}

type Tab = 'notifications' | 'friends' | 'messages';

const ago = (iso: string): string => {
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const message = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

/**
 * The bell: notifications, friends and private messages of the signed-in
 * account. `onLink` handles the places a notification can point at that this
 * panel does not show itself (`invitations`, `project:<id>`, `game:<slug>`).
 */
export function SocialBell({ onLink }: { onLink: (link: string) => void }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('notifications');
  const [summary, setSummary] = useState<Summary>({ notifications: 0, messages: 0, friendRequests: 0, invitations: 0 });
  const [chatWith, setChatWith] = useState<Person | null>(null);
  const box = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      setSummary(await api.get<Summary>(social('/social/summary')));
    } catch {
      // Offline or signed out: the bell just shows nothing new.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 20_000);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  function follow(link: string | null) {
    if (!link) return;
    if (link === 'friends') return setTab('friends');
    if (link.startsWith('messages:')) {
      setTab('messages');
      setChatWith({ id: Number(link.slice(9)), username: '', avatar: null });
      return;
    }
    setOpen(false);
    onLink(link);
  }

  const badge = summary.notifications;
  return (
    <div className="soc-bell" ref={box}>
      <button type="button" className="ghost soc-bell-button" aria-label={`Notifications${badge ? `, ${badge} new` : ''}`} onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">🔔</span>
        {badge > 0 && <span className="soc-badge">{badge > 99 ? '99+' : badge}</span>}
      </button>
      {open && (
        <div className="soc-panel" role="dialog" aria-label="Notifications, friends and messages">
          <div className="soc-tabs">
            {(['notifications', 'friends', 'messages'] as const).map((t) => (
              <button key={t} type="button" className={tab === t ? 'is-on' : ''} onClick={() => (setTab(t), t !== 'messages' && setChatWith(null))}>
                {t === 'notifications' ? 'Notifications' : t === 'friends' ? 'Friends' : 'Messages'}
                {t === 'messages' && summary.messages > 0 && <span className="soc-badge">{summary.messages}</span>}
                {t === 'friends' && summary.friendRequests > 0 && <span className="soc-badge">{summary.friendRequests}</span>}
              </button>
            ))}
          </div>
          {tab === 'notifications' && <Notifications onFollow={follow} onChanged={refresh} />}
          {tab === 'friends' && (
            <FriendsTab
              onMessage={(p) => {
                setTab('messages');
                setChatWith(p);
              }}
              onChanged={refresh}
            />
          )}
          {tab === 'messages' && <MessagesTab chatWith={chatWith} onOpen={setChatWith} onChanged={refresh} />}
        </div>
      )}
    </div>
  );
}

function Notifications({ onFollow, onChanged }: { onFollow: (link: string | null) => void; onChanged: () => void }) {
  const [notes, setNotes] = useState<Note[] | null>(null);

  const load = useCallback(async () => {
    try {
      setNotes((await api.get<{ notifications: Note[] }>(social('/notifications'))).notifications);
    } catch {
      setNotes([]);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function read(ids?: number[]) {
    await api.post(social('/notifications/read'), ids ? { ids } : {}).catch(() => undefined);
    await load();
    onChanged();
  }

  if (notes === null) return <p className="muted soc-pad">Loading…</p>;
  return (
    <div>
      <div className="soc-bar">
        <span className="muted small">{notes.filter((n) => !n.read).length} new</span>
        <button type="button" className="ghost tiny" disabled={!notes.some((n) => !n.read)} onClick={() => void read()}>
          Mark all read
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="muted soc-pad">Nothing yet. Messages, invitations, friend requests and featured games show up here.</p>
      ) : (
        <ul className="soc-notes">
          {notes.map((n) => (
            <li key={n.id} className={n.read ? '' : 'is-new'}>
              <button
                type="button"
                onClick={() => {
                  if (!n.read) void read([n.id]);
                  onFollow(n.link);
                }}
              >
                <strong>{n.title}</strong>
                {n.body && <span className="muted">{n.body}</span>}
                <span className="muted small">{ago(n.createdAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FriendsTab({ onMessage, onChanged }: { onMessage: (p: Person) => void; onChanged: () => void }) {
  const [lists, setLists] = useState<Friends | null>(null);
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLists(await api.get<Friends>(social('/friends')));
    } catch {
      setLists({ friends: [], incoming: [], outgoing: [] });
    }
  }, []);
  useEffect(() => void load(), [load]);

  async function act(run: () => Promise<unknown>, fallback: string) {
    setError(null);
    setNote(null);
    try {
      await run();
      await load();
      onChanged();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    await act(async () => {
      const r = await api.post<{ state: string; person: Person }>(social('/friends/request'), { username: username.trim() });
      setNote(r.state === 'friends' ? `You and ${r.person.username} are friends now.` : `Friend request sent to ${r.person.username}.`);
      setUsername('');
    }, 'Could not send the request.');
  }

  return (
    <div className="soc-pad">
      <form className="soc-row" onSubmit={add}>
        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Add a friend by username" maxLength={60} />
        <button className="primary" type="submit" disabled={!username.trim()}>
          Add
        </button>
      </form>
      {error && <p className="login-error">{error}</p>}
      {note && <p className="muted small">{note}</p>}
      {lists === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          {lists.incoming.length > 0 && (
            <>
              <h4>Friend requests</h4>
              <ul className="soc-list">
                {lists.incoming.map((p) => (
                  <li key={p.id}>
                    <strong>{p.username}</strong>
                    <span className="soc-actions">
                      <button type="button" className="primary tiny" onClick={() => void act(() => api.post(social(`/friends/${p.id}/accept`)), 'Could not accept.')}>
                        Accept
                      </button>
                      <button type="button" className="ghost tiny" onClick={() => void act(() => api.del(social(`/friends/${p.id}`)), 'Could not decline.')}>
                        Decline
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <h4>Friends ({lists.friends.length})</h4>
          <ul className="soc-list">
            {lists.friends.map((p) => (
              <li key={p.id}>
                <strong>{p.username}</strong>
                <span className="soc-actions">
                  <button type="button" className="ghost tiny" onClick={() => onMessage(p)}>
                    Message
                  </button>
                  <button type="button" className="ghost tiny danger" onClick={() => window.confirm(`Remove ${p.username} from your friends?`) && void act(() => api.del(social(`/friends/${p.id}`)), 'Could not remove.')}>
                    Remove
                  </button>
                </span>
              </li>
            ))}
            {lists.friends.length === 0 && <li className="muted">No friends yet. Add someone by username.</li>}
          </ul>
          {lists.outgoing.length > 0 && (
            <>
              <h4>Waiting for an answer</h4>
              <ul className="soc-list">
                {lists.outgoing.map((p) => (
                  <li key={p.id}>
                    <span>{p.username}</span>
                    <button type="button" className="ghost tiny" onClick={() => void act(() => api.del(social(`/friends/${p.id}`)), 'Could not cancel.')}>
                      Cancel
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

function MessagesTab({ chatWith, onOpen, onChanged }: { chatWith: Person | null; onOpen: (p: Person | null) => void; onChanged: () => void }) {
  const [convos, setConvos] = useState<Convo[] | null>(null);

  const load = useCallback(async () => {
    try {
      setConvos((await api.get<{ conversations: Convo[] }>(social('/messages'))).conversations);
    } catch {
      setConvos([]);
    }
  }, []);

  useEffect(() => {
    if (chatWith) return;
    void load();
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [load, chatWith]);

  if (chatWith) return <Thread friend={chatWith} onBack={() => (onOpen(null), void load(), onChanged())} onChanged={onChanged} />;
  if (convos === null) return <p className="muted soc-pad">Loading…</p>;
  return convos.length === 0 ? (
    <p className="muted soc-pad">No messages yet. Open the Friends tab and press Message on a friend to start.</p>
  ) : (
    <ul className="soc-notes">
      {convos.map((c) => (
        <li key={c.friend.id} className={c.unread ? 'is-new' : ''}>
          <button type="button" onClick={() => onOpen(c.friend)}>
            <strong>
              {c.friend.username} {c.unread > 0 && <span className="soc-badge">{c.unread}</span>}
            </strong>
            <span className="muted">
              {c.last.fromMe ? 'You: ' : ''}
              {c.last.text}
            </span>
            <span className="muted small">{ago(c.last.at)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Thread({ friend, onBack, onChanged }: { friend: Person; onBack: () => void; onChanged: () => void }) {
  const [name, setName] = useState(friend.username);
  const [messages, setMessages] = useState<Msg[] | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ friend: Person | null; messages: Msg[] }>(social(`/messages/${friend.id}`));
      if (r.friend) setName(r.friend.username);
      setMessages(r.messages);
      setError(null);
      onChanged();
    } catch (e) {
      setError(message(e, 'Could not open this chat.'));
      setMessages([]);
    }
  }, [friend.id, onChanged]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5_000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => end.current?.scrollIntoView({ block: 'end' }), [messages?.length]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    try {
      await api.post(social(`/messages/${friend.id}`), { text: body });
      setText('');
      await load();
    } catch (err) {
      setError(message(err, 'Could not send that.'));
    }
  }

  return (
    <div className="soc-thread">
      <div className="soc-bar">
        <button type="button" className="ghost tiny" onClick={onBack}>
          ← Back
        </button>
        <strong>{name || 'Chat'}</strong>
      </div>
      <div className="soc-messages">
        {messages === null && <p className="muted">Loading…</p>}
        {messages?.map((m) => (
          <div key={m.id} className={`soc-msg ${m.fromMe ? 'mine' : ''}`} title={new Date(m.at).toLocaleString()}>
            {m.text}
          </div>
        ))}
        <div ref={end} />
      </div>
      {error && <p className="login-error">{error}</p>}
      <form className="soc-row" onSubmit={send}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message" maxLength={1000} />
        <button className="primary" type="submit" disabled={!text.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
